<template>
  <v-layout align-center>
    <v-flex xs12 sm10 md8 lg6>
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="loading">
            <spinner />
          </div>
          <form
            v-else
            v-scroll-to-error
            class="mobile-button-container"
            @submit.prevent="submitForm"
          >
            <div class="title-screen">
              <span>Create gratis ticket</span>
            </div>
            <gratis-ticket-form
              v-if="activeStep === 1"
              :vs="$v"
              :currency="event.currency"
              :initial-values="form"
              :has-parter-tickets="hasParterTickets"
              :is-floor-plan="event.extraModules.floorPlan"
              :max-initial-quantity="calculateMaxInitialQuantity()"
            />
            <div v-else>
              <gratis-ticket-floor-plan
                ref="GratisTicketFloorPlan"
                :initial-venue="initialVenue"
                :init-venue-availability="venueAvailability"
                :initial-values="form"
              />
            </div>
            <div class="buttons-container">
              <main-button v-if="activeStep === 1" text="Cancel" flat @click.prevent="goBack" />
              <main-button v-else text="Back" flat @click.prevent="activeStep = 1" />
              <main-button
                v-if="form.ticketType === 'Floor Plan' && activeStep === 1"
                text="Next"
                @click.prevent="toFloorPlan"
              />
              <main-button
                v-else
                text="Create & download tickets"
                depressed
                :disabled="!user.isVerifiedByCoreEvent"
                :loading="downloading"
              />
              <a v-show="false" id="download-ticket" />
            </div>
          </form>
        </v-flex>
        <simple-snackbar :open.sync="snackbarOpen" :message-prop="snackbarMessage" color="red" />
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import GratisTicketForm from './gratis-ticket-form'
import GratisTicketFloorPlan from './gratis-ticket-floor-plan'
import MainButton from './../shared/main-button'
import { submitGuard } from './../../utils'
import { createGratisTickets } from '../../http'
import Spinner from '../shared/spinner.vue'
import { required, maxLength, minValue, requiredIf, maxValue } from 'vuelidate/lib/validators'
import { GET_EVENT_DETAILS_WITH_ORDER_ITEMS } from '@/graphql/event.graphql'
import axios from 'axios'
import { GET_EVENT_FLOOR_PLAN } from '@/graphql/event.graphql'
import { updateVenueAvailability } from '@/http'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'

export default {
  name: 'create-gratis-ticket',
  components: {
    GratisTicketForm,
    GratisTicketFloorPlan,
    MainButton,
    Spinner,
    SimpleSnackbar,
  },
  apollo: {
    getEventBySlug: {
      query: GET_EVENT_DETAILS_WITH_ORDER_ITEMS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      async result({ loading, data }) {
        if (!loading) {
          const { getEvent: event, getOrderItems: orderItems } = data
          this.event = event
          this.orderItems = orderItems.items
          this.hasParterTickets = this.event.extraModules.floorPlan
            ? orderItems.hasParterTickets
            : true
          this.form = { price: '0', selectedSeats: [] }
          this.loading = false
        }
      },
    },
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
    user: Object,
    organizationSlug: {
      default: null,
      type: String,
    },
  },
  data: () => ({
    event: {},
    form: {},
    loading: true,
    downloading: false,
    activeStep: 1,
    venueAvailability: {},
    initialVenue: {},
    snackbarOpen: false,
    snackbarMessage: '',
    orderItems: [],
    hasParterTickets: false,
  }),
  validations() {
    return {
      form: {
        name: { required },
        price: { required },
        initialQuantity: this.hasEventMaxTicketsNumber()
          ? {
              required,
              maxValue: maxValue(this.calculateMaxInitialQuantity()),
              minValue: minValue(1),
            }
          : { required, minValue: minValue(1) },
        description: { maxLength: maxLength(256) },
        ticketType: { required: requiredIf(() => this.event.extraModules.floorPlan) },
      },
    }
  },
  methods: {
    async submitForm() {
      await submitGuard(this, async () => {
        if (this.activeStep === 2) {
          if (this.form.selectedSeats.length === this.form.initialQuantity) {
            this.downloading = true
            const { errors } = await updateVenueAvailability({
              eventId: this.eventId,
              seats: this.form.selectedSeats,
              organizationSlug: this.organizationSlug,
              state: 'SOLD',
              venueId: this.initialVenue.id,
            })
            if (errors) {
              this.snackbarMessage = errors[0].message
              this.snackbarOpen = true
              this.downloading = false
              await this.getFloorPlan()
              this.$refs.GratisTicketFloorPlan.refreshSeats()
            } else await this.createTickets()
          } else {
            this.snackbarMessage = "Number of selected tickets doesn't match defined quantity"
            this.snackbarOpen = true
          }
        } else {
          this.downloading = true
          await this.createTickets()
        }
      })
    },
    async createTickets() {
      const { ticketType, selectedSeats, ...rest } = this.form
      const orderUUID = await createGratisTickets(
        this.eventId,
        { ...rest, currency: this.event.currency },
        ticketType === 'Parter ticket' || !selectedSeats.length ? null : selectedSeats,
      )
      if (orderUUID) {
        await this.downloadTickets(orderUUID)
        this.downloading = false
        this.goBack()
      }
    },
    async toFloorPlan() {
      await submitGuard(this, async () => {
        await this.getFloorPlan()
        this.activeStep = 2
      })
    },
    async getFloorPlan() {
      const { data } = await this.$apollo.query({
        query: GET_EVENT_FLOOR_PLAN,
        variables: { eventId: this.eventId },
      })
      if (data) {
        this.initialVenue = data.getVenues.find(v => v.id === data.getEventFloorPlan.venueId)
        this.venueAvailability =
          data.getEventFloorPlan.venueAvailability &&
          data.getEventFloorPlan.venueAvailability.availability
      }
    },
    async downloadTickets(uuid) {
      const { data: ticket } = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/files/tickets/${uuid}`,
      )
      const byteString = window.atob(ticket.body)
      const arrayBuffer = new ArrayBuffer(byteString.length)
      const int8Array = new Uint8Array(arrayBuffer)
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([int8Array], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const downloadLink = document.getElementById('download-ticket')
      downloadLink.href = url
      downloadLink.setAttribute('download', 'CoreEvent-karte.pdf')
      downloadLink.click()
    },
    hasEventMaxTicketsNumber() {
      return !this.event.extraModules.floorPlan && !!this.event.audienceCapacity
    },
    calculateMaxInitialQuantity() {
      if (!this.event.audienceCapacity) {
        return null
      }

      return this.event.audienceCapacity
    },
  },
}
</script>

<style scoped>
.buttons-container {
  display: flex;
  justify-content: space-between;
  margin-top: 4.6875rem;
}
@media screen and (max-width: 400px) {
  .buttons-container {
    flex-direction: column-reverse;
    row-gap: 1rem;
  }
}
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }
  .mobile-button-container {
    margin-top: 1.5rem;
  }
}
</style>
